import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Garden, Schedule } from '@/types';
import { mockSchedules, mockControllers } from '@/data/mockData';
import { Clock, Droplets } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GardenScheduleCalendarProps {
  garden: Garden;
}

const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

interface ScheduleEvent {
  schedule: Schedule;
  startHour: number;
  startMinute: number;
  duration: number;
  zones: string[];
}

export function GardenScheduleCalendar({ garden }: GardenScheduleCalendarProps) {
  // Get all schedules that affect this garden
  const gardenSchedules = useMemo(() => {
    // Direct garden schedules
    const directSchedules = mockSchedules.filter(s => s.gardenId === garden.id && s.enabled);
    
    // Schedules assigned to zones via controller/valve
    const zoneScheduleIds = new Set<string>();
    garden.zones.forEach(zone => {
      zone.scheduleAssignments?.forEach(sa => {
        zoneScheduleIds.add(sa.scheduleId);
      });
    });
    
    const zoneSchedules = mockSchedules.filter(s => zoneScheduleIds.has(s.id) && s.enabled);
    
    // Merge and deduplicate
    const allSchedules = [...directSchedules];
    zoneSchedules.forEach(zs => {
      if (!allSchedules.some(s => s.id === zs.id)) {
        allSchedules.push(zs);
      }
    });
    
    return allSchedules;
  }, [garden]);

  // Build schedule events by day
  const eventsByDay = useMemo(() => {
    const result: Record<number, ScheduleEvent[]> = {};
    
    DAYS_OF_WEEK.forEach((_, dayIndex) => {
      result[dayIndex] = [];
    });

    gardenSchedules.forEach(schedule => {
      const [hours, minutes] = schedule.startTime.split(':').map(Number);
      
      // Find which zones this schedule affects
      const affectedZones: string[] = [];
      garden.zones.forEach(zone => {
        const hasAssignment = zone.scheduleAssignments?.some(sa => sa.scheduleId === schedule.id);
        if (hasAssignment) {
          affectedZones.push(zone.name);
        }
      });

      // Also check via valve assignments
      if (schedule.assignments && schedule.assignments.length > 0) {
        schedule.assignments.forEach(assignment => {
          const controller = mockControllers.find(c => c.id === assignment.controllerId);
          if (controller) {
            garden.zones.forEach(zone => {
              const hasController = zone.controllerAssignments.some(
                ca => ca.controllerId === assignment.controllerId
              );
              if (hasController && !affectedZones.includes(zone.name)) {
                affectedZones.push(zone.name);
              }
            });
          }
        });
      }

      schedule.daysOfWeek.forEach(day => {
        result[day].push({
          schedule,
          startHour: hours,
          startMinute: minutes,
          duration: schedule.duration,
          zones: affectedZones.length > 0 ? affectedZones : ['General'],
        });
      });
    });

    // Sort by start time
    Object.values(result).forEach(events => {
      events.sort((a, b) => {
        const aTime = a.startHour * 60 + a.startMinute;
        const bTime = b.startHour * 60 + b.startMinute;
        return aTime - bTime;
      });
    });

    return result;
  }, [gardenSchedules, garden]);

  // Calculate timeline range (earliest start - latest end)
  const timeRange = useMemo(() => {
    let minHour = 24;
    let maxHour = 0;

    Object.values(eventsByDay).forEach(events => {
      events.forEach(event => {
        minHour = Math.min(minHour, event.startHour);
        const endHour = Math.ceil((event.startHour * 60 + event.startMinute + event.duration) / 60);
        maxHour = Math.max(maxHour, endHour);
      });
    });

    // Default range if no schedules
    if (minHour > maxHour) {
      minHour = 6;
      maxHour = 22;
    }

    // Add padding
    minHour = Math.max(0, minHour - 1);
    maxHour = Math.min(24, maxHour + 1);

    return { minHour, maxHour };
  }, [eventsByDay]);

  const visibleHours = HOURS.slice(timeRange.minHour, timeRange.maxHour);

  if (gardenSchedules.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Droplets className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">
            No hay programaciones activas para este jardín
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          Calendario Semanal de Riegos
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="w-full">
          <div className="min-w-[600px]">
            {/* Header with days */}
            <div className="grid grid-cols-8 border-b bg-muted/30">
              <div className="p-2 text-xs font-medium text-muted-foreground text-center border-r">
                Hora
              </div>
              {DAYS_OF_WEEK.map((day, index) => (
                <div 
                  key={day} 
                  className={cn(
                    "p-2 text-xs font-medium text-center",
                    index === new Date().getDay() && "bg-primary/10 text-primary"
                  )}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Time grid */}
            <div className="relative">
              {visibleHours.map(hour => (
                <div key={hour} className="grid grid-cols-8 border-b min-h-[40px]">
                  {/* Hour label */}
                  <div className="p-1 text-xs text-muted-foreground text-center border-r flex items-center justify-center">
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  
                  {/* Day columns */}
                  {DAYS_OF_WEEK.map((_, dayIndex) => {
                    const dayEvents = eventsByDay[dayIndex].filter(event => 
                      event.startHour === hour
                    );
                    
                    return (
                      <div 
                        key={dayIndex} 
                        className={cn(
                          "relative p-0.5 border-r last:border-r-0",
                          dayIndex === new Date().getDay() && "bg-primary/5"
                        )}
                      >
                        {dayEvents.map((event, eventIndex) => (
                          <div
                            key={`${event.schedule.id}-${eventIndex}`}
                            className="mb-0.5 p-1 rounded text-xs bg-primary/20 border border-primary/30 overflow-hidden"
                            title={`${event.schedule.name}\n${event.schedule.startTime} - ${event.duration}min\n${event.zones.join(', ')}`}
                          >
                            <div className="font-medium truncate text-primary">
                              {event.schedule.startTime}
                            </div>
                            <div className="truncate text-muted-foreground">
                              {event.schedule.name}
                            </div>
                            <div className="truncate text-muted-foreground/70">
                              {event.duration}min
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>

        {/* Legend */}
        <div className="p-3 border-t bg-muted/30">
          <div className="flex flex-wrap gap-2">
            {gardenSchedules.map(schedule => (
              <Badge 
                key={schedule.id} 
                variant="outline" 
                className="text-xs gap-1"
              >
                <div className="w-2 h-2 rounded-full bg-primary" />
                {schedule.name}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
